/**
 * Sector operations — Biomedical Waste (BMW) Common Data Model and Validators.
 * R39 C5.a2 (plan docs/plans/sector-operations.md §2 SO-P252, H§5).
 * Sector-neutral core (Part M M7 extends later).
 */

export const BMW_CATEGORIES = ['YELLOW', 'RED', 'WHITE', 'BLUE'] as const
export type BmwCategory = (typeof BMW_CATEGORIES)[number]

export const BMW_DEFAULT_STORAGE_HOURS = 48
export const BMW_MIN_STORAGE_HOURS = 12
export const BMW_MAX_STORAGE_HOURS = 96

export const BMW_DEFAULT_UNUSUAL_KG = 100
export const BMW_MIN_UNUSUAL_KG = 1
export const BMW_MAX_UNUSUAL_KG = 1000

export const BMW_MANIFEST_REF_REGEX = /^[A-Za-z0-9/_-]{3,40}$/

export const MAX_BMW_COLLECTORS = 5
export const BMW_E164_PHONE_REGEX = /^\+[1-9]\d{7,14}$/

export interface BmwCollector {
  name: string
  cbwtfName: string
  phone: string
}

export interface BmwSettingsDoc {
  workspaceId: string
  storageHours: number
  unusualKgPerCategory: number
  requirePreTreatmentPhoto?: boolean
  collectors?: BmwCollector[]
  updatedAt?: unknown
  updatedBy?: string
}

export interface BmwCorrection {
  correctionId: string
  reason: string
  correctedBy: string
  correctedAt: unknown
  before: {
    weights: Record<BmwCategory, number>
    bagCounts?: Partial<Record<BmwCategory, number>>
  }
  after: {
    weights: Record<BmwCategory, number>
    bagCounts?: Partial<Record<BmwCategory, number>>
  }
}

export type BmwDayState = 'DRAFT' | 'SUBMITTED'

export const BMW_BARCODE_REGEX = /^[A-Za-z0-9-]{4,40}$/
export const MAX_BMW_BAGS_PER_DAY = 300
export const MAX_BMW_BAG_KG = 300

export interface BmwBag {
  barcode: string
  color: BmwCategory
  weightKg: number
  pickedUpAt?: unknown
  pickupId?: string | null
}

export const BMW_PHOTO_PATH_REGEX = /^A4\/[A-Za-z0-9_-]+\/bmw\/[A-Za-z0-9_.-]+$/

export interface BmwDayDoc {
  id?: string
  workspaceId: string
  placeId: string
  dayKey: string
  weights: Record<BmwCategory, number>
  bagCounts?: Partial<Record<BmwCategory, number>>
  preTreatmentPhotos?: string[]
  bags?: BmwBag[]
  state: BmwDayState
  enteredBy: string
  enteredAt: unknown
  lockedAt?: unknown
  lockedBy?: string
  corrections?: BmwCorrection[]
  pickupId?: string | null
  createdAt?: unknown
  updatedAt?: unknown
}

export interface BmwPickupDoc {
  id?: string
  workspaceId: string
  placeId: string
  pickedAt: unknown
  manifestRef: string
  vehicleRef?: string | null
  bagCounts?: Partial<Record<BmwCategory, number>>
  coveredDayKeys: string[]
  recordedBy: string
  recordedAt: unknown
  otpVerifiedPhone?: string | null
  bagBarcodes?: string[]
  receiptPhoto?: string | null
  receiptAttachedAt?: unknown
  receiptAttachedBy?: string | null
  opId?: string
  createdAt?: unknown
  updatedAt?: unknown
}

export interface BmwHandoverDoc {
  id: string
  workspaceId: string
  placeId: string
  tokenHash: string
  expiresAtMs: number
  status: 'ACTIVE' | 'CONFIRMED' | 'EXPIRED'
  selectedBarcodes?: string[] | null
  createdByUid: string
  createdAtMs: number
  confirmedAtMs?: number
  otpVerifiedPhone?: string
  pickupId?: string
}

export interface StartBmwHandoverInput {
  workspaceId: string
  placeId: string
  bagBarcodes?: string[]
}

export interface StartBmwHandoverResult {
  handoverId: string
  token: string
  expiresAtMs: number
  url: string
}

export interface GetBmwHandoverInfoInput {
  token: string
}

export interface GetBmwHandoverInfoResult {
  handoverId: string
  workspaceName: string
  placeName: string
  bags: Array<{ barcode: string; color: BmwCategory; weightKg: number }>
  maskedCollectorPhones: string[]
}

export interface PrepareBmwHandoverOtpInput {
  token: string
  phone: string
}

export interface PrepareBmwHandoverOtpResult {
  success: boolean
  attemptsRemaining: number
}

export interface ConfirmBmwHandoverInput {
  token: string
  confirmedBarcodes: string[]
}

export interface ConfirmBmwHandoverResult {
  pickupId: string
  confirmedCount: number
  outstandingCount: number
}

export interface AttachBmwPickupReceiptInput {
  workspaceId: string
  pickupId: string
  receiptPhoto: string
}

export interface AttachBmwPickupReceiptResult {
  success: boolean
}

export interface BmwStatusDay {
  dayKey: string
  state: 'NONE' | 'DRAFT' | 'LOCKED'
  weights?: Record<BmwCategory, number>
  bagCounts?: Partial<Record<BmwCategory, number>>
  preTreatmentPhotos?: string[]
  bags?: BmwBag[]
  submittedAt?: unknown
}

export interface BmwStatusResult {
  placeId: string
  oldestUncoveredDayKey: string | null
  hoursStored: number
  storageHoursLimit: number
  status: 'OK' | 'EXCEEDED' | 'NO_UNCOVERED_WASTE'
  oldestSubmittedAt?: unknown
  day?: BmwStatusDay
  canWrite?: boolean
  canCorrect?: boolean
  settings?: BmwSettingsDoc
}

export interface BmwMonthSummary {
  month: string // YYYY-MM
  weights: Record<BmwCategory, number>
  bagCounts: Record<BmwCategory, number>
  pickupsCount: number
  manifestRefs: string[]
}

export interface BmwSummaryResult {
  workspaceId: string
  kind: 'ANNUAL_FORM_IV' | 'MONTHLY_DISCLOSURE'
  year: number
  month?: number
  draftLabel: string // "Draft for your review before submission"
  months: BmwMonthSummary[]
  totals: {
    weights: Record<BmwCategory, number>
    bagCounts: Record<BmwCategory, number>
    totalPickups: number
  }
}

// ---------------------------------------------------------------------------
// Pure Validators & Helpers
// ---------------------------------------------------------------------------

export function isValidBmwWeight(weight: unknown): boolean {
  if (typeof weight !== 'number' || isNaN(weight) || !isFinite(weight)) return false
  if (weight < 0 || weight > 9999.999) return false
  // At most 3 decimal places
  const str = weight.toString()
  const decimalPart = str.split('.')[1]
  return !decimalPart || decimalPart.length <= 3
}

export function validateBmwWeights(
  weights: unknown,
): Record<BmwCategory, number> {
  if (!weights || typeof weights !== 'object') {
    throw new Error('Weights must be an object with values for YELLOW, RED, WHITE, and BLUE.')
  }
  const obj = weights as Record<string, unknown>
  const result: Record<BmwCategory, number> = {
    YELLOW: 0,
    RED: 0,
    WHITE: 0,
    BLUE: 0,
  }

  for (const cat of BMW_CATEGORIES) {
    const val = obj[cat] ?? 0
    if (!isValidBmwWeight(val)) {
      throw new Error(`Invalid weight for ${cat}: must be between 0 and 9999.999 with at most 3 decimal places.`)
    }
    result[cat] = Math.round((val as number) * 1000) / 1000
  }

  return result
}

export function validateBmwBagCounts(
  bagCounts: unknown,
): Partial<Record<BmwCategory, number>> | undefined {
  if (bagCounts === undefined || bagCounts === null) return undefined
  if (typeof bagCounts !== 'object') {
    throw new Error('bagCounts must be an object.')
  }
  const obj = bagCounts as Record<string, unknown>
  const result: Partial<Record<BmwCategory, number>> = {}

  for (const cat of BMW_CATEGORIES) {
    const val = obj[cat]
    if (val !== undefined && val !== null) {
      if (typeof val !== 'number' || !Number.isInteger(val) || val < 0 || val > 100000) {
        throw new Error(`Invalid bagCount for ${cat}: must be an integer >= 0.`)
      }
      result[cat] = val
    }
  }

  return result
}

export function hasUnusualWeight(
  weights: Record<BmwCategory, number>,
  unusualLimitKg: number,
): boolean {
  for (const cat of BMW_CATEGORIES) {
    if (weights[cat] > unusualLimitKg) {
      return true
    }
  }
  return false
}

export function validateManifestRef(ref: unknown): string {
  if (typeof ref !== 'string' || !BMW_MANIFEST_REF_REGEX.test(ref.trim())) {
    throw new Error('manifestRef must be 3-40 alphanumeric characters, dashes, slashes, or underscores.')
  }
  return ref.trim()
}

export function sanitizeCsvFormulaPrefix(value: unknown): string {
  const str = String(value ?? '')
  if (/^[=+\-@\t\r]/.test(str)) {
    return `'${str}`
  }
  return str
}

export function isValidBmwPhotoPath(path: unknown): boolean {
  return typeof path === 'string' && BMW_PHOTO_PATH_REGEX.test(path.trim())
}

export function validateBmwCollector(collector: unknown): BmwCollector {
  if (!collector || typeof collector !== 'object') {
    throw new Error('Collector must be an object.')
  }
  const obj = collector as Record<string, unknown>
  const name = typeof obj.name === 'string' ? obj.name.trim() : ''
  const cbwtfName = typeof obj.cbwtfName === 'string' ? obj.cbwtfName.trim() : ''
  const phone = typeof obj.phone === 'string' ? obj.phone.trim() : ''

  if (!name || name.length > 80) {
    throw new Error('Collector name is required and must be at most 80 characters.')
  }
  if (!cbwtfName || cbwtfName.length > 80) {
    throw new Error('CBWTF name is required and must be at most 80 characters.')
  }
  if (!phone || !BMW_E164_PHONE_REGEX.test(phone)) {
    throw new Error('Collector phone must be a valid E.164 number (e.g. +919876543210).')
  }

  return { name, cbwtfName, phone }
}

export function validateBmwCollectors(collectors: unknown): BmwCollector[] | undefined {
  if (collectors === undefined || collectors === null) return undefined
  if (!Array.isArray(collectors)) {
    throw new Error('collectors must be an array.')
  }
  if (collectors.length > MAX_BMW_COLLECTORS) {
    throw new Error(`At most ${MAX_BMW_COLLECTORS} collectors allowed.`)
  }
  return collectors.map(validateBmwCollector)
}

export function isValidBmwBarcode(barcode: unknown): boolean {
  return typeof barcode === 'string' && BMW_BARCODE_REGEX.test(barcode.trim())
}

export function validateBmwBag(bag: unknown): BmwBag {
  if (!bag || typeof bag !== 'object') {
    throw new Error('Bag must be an object.')
  }
  const obj = bag as Record<string, unknown>
  const barcode = typeof obj.barcode === 'string' ? obj.barcode.trim() : ''
  const color = obj.color as BmwCategory
  const weightKg = Number(obj.weightKg)

  if (!isValidBmwBarcode(barcode)) {
    throw new Error('Barcode must be 4–40 alphanumeric characters or hyphens.')
  }
  if (!BMW_CATEGORIES.includes(color)) {
    throw new Error(`Invalid color: must be one of ${BMW_CATEGORIES.join(', ')}.`)
  }
  if (!isValidBmwWeight(weightKg) || weightKg <= 0 || weightKg > MAX_BMW_BAG_KG) {
    throw new Error(`Bag weight must be greater than 0 and at most ${MAX_BMW_BAG_KG} kg.`)
  }

  return {
    barcode,
    color,
    weightKg: Math.round(weightKg * 1000) / 1000,
    pickedUpAt: obj.pickedUpAt ?? undefined,
    pickupId: typeof obj.pickupId === 'string' ? obj.pickupId : null,
  }
}

export function validateBmwBags(bags: unknown): BmwBag[] | undefined {
  if (bags === undefined || bags === null) return undefined
  if (!Array.isArray(bags)) {
    throw new Error('bags must be an array.')
  }
  if (bags.length > MAX_BMW_BAGS_PER_DAY) {
    throw new Error(`At most ${MAX_BMW_BAGS_PER_DAY} bags allowed per day.`)
  }
  const validated = bags.map(validateBmwBag)
  const barcodes = new Set<string>()
  for (const b of validated) {
    if (barcodes.has(b.barcode)) {
      throw new Error(`Duplicate bag barcode within day: ${b.barcode}`)
    }
    barcodes.add(b.barcode)
  }
  return validated
}

